import React, { useState, useEffect, useMemo } from 'react';
import Select from 'react-select';
import _ from 'lodash';
import { Card, CardHeader, CardBody } from 'reactstrap';
import TightTable from 'components/TightTable';
import TightChart from 'components/TightChart';
import TimePeriod, { formatDates } from 'components/TimePeriod';
import { AutoColumns } from 'components/Columns';
import { useArrayState } from 'components/CustomHooks';
import { optionizeValue, optionizeAll, getValue, getDate } from 'helpers/select';
import { toDeci, toDeciTable, toPercTable, tablePresets, getDataType } from 'helpers/formatter';
import { removeEmptyCols } from 'helpers/data-frame';

const ratios = ['Beta', 'Alpha', 'R2', 'Correlation', "Jensen's Alpha", 'Treynor Ratio'];

function MarketRisk({ data: { data, table_dates }, params, session }) {
  const [filteredDF, setFilteredDF] = useState(null);
  const [ratioOptions, setRatioOptions] = useState(optionizeAll(ratios));
  const [selectedRatioOption, setSelectedRatioOption] = useState(optionizeValue(ratios[0]));

  useEffect(() => {
    if (!data) return;

    const _filteredDF = removeEmptyCols(data, ['15Y', '20Y']);
    setFilteredDF(_filteredDF);
  }, [data]);

  if (!filteredDF) return null;

  return (
    <>
      <Card>
        <CardHeader tag="h4" className="mt-0 text-center">
          Market Risk
          <div className="tw-text-lg">{params.product}</div>
          <TimePeriod dates={table_dates} prefix="Time Period:" />
        </CardHeader>
        <CardBody>
          <TightTable
            suffix="risk-performance-market-risk"
            data={filteredDF}
            indexName=""
            indexWidth="250px"
            cellFormatter={(value, row, col) => {
              const formatter = tablePresets[row];
              if (formatter) return formatter(value);

              return toPercTable(value);
            }}
            title={['Market Risk', params.product, formatDates(table_dates)]}
          />
        </CardBody>
      </Card>

      <div className="tw-grid tw-grid-cols-64">
        <div className="tw-col-span-6">
          <div className="tw-text-left tw-font-bold">SELECT METRIC</div>
          <Select
            className="react-select mw-400px"
            value={selectedRatioOption}
            onChange={setSelectedRatioOption}
            options={ratioOptions}
          />
        </div>
      </div>

      <div className="tw-grid tw-grid-cols-6 tw-gap-4">
        <div className="tw-col-span-3">
          {selectedRatioOption && (
            <Card>
              <CardHeader tag="h4" className="mt-0 text-center">
                {selectedRatioOption.label}
                <div className="tw-text-lg">{params.product}</div>
              </CardHeader>
              <CardBody>
                <TightChart
                  title={selectedRatioOption.label}
                  data={filteredDF}
                  cols={[selectedRatioOption.value]}
                  flip={true}
                  wraplabelX={true}
                  dataType={getDataType(selectedRatioOption.label)}
                />
              </CardBody>
            </Card>
          )}
        </div>
        <div className="tw-col-span-3">
          <Card>
            <CardHeader tag="h4" className="mt-0 text-center">
              {params.product}
            </CardHeader>
            <CardBody>
              <TightChart
                title={params.product}
                data={filteredDF}
                cols={['Unsystematic Risk', 'Systematic Risk']}
                flip={true}
                dataType="percentage"
                options={{
                  indexAxis: 'y',
                  scales: {
                    x: {
                      stacked: true,
                    },
                    y: {
                      stacked: true,
                    },
                  },
                }}
              />
            </CardBody>
          </Card>
        </div>
      </div>
    </>
  );
}

export default MarketRisk;
